import { Modal, message } from "antd";
import { ReactComponent as Cross } from "assets/svg/closeBox.svg";
import { ReactComponent as ArrowLeft } from "assets/svg/ArrowLeftBlue.svg";
import { ReactComponent as Calender } from "assets/svg/calander.svg";

import { useEffect, useState } from "react";
import { UploadFile } from "antd/es/upload";

// @ts-ignore
import styles from "./styles.module.scss";

import type { UploadProps } from "antd";
import { Upload } from "antd";

import { ReactComponent as DownloadUp } from "assets/svg/downloadUp.svg";
// @ts-ignore
import template from "../../../templates/ProjectTemplate.csv";
import { useDispatch, useSelector } from "react-redux";
import {
  uploadCSV,
  BulkAriaProjectPlan,
  getPredefinedMetrics,
  // @ts-ignore
} from "modules/actions/PlanActions";
import { LoadingOutlined } from "@ant-design/icons";
import { formatTimeString } from "../../../utils/helper";
import moment from "moment";
import { useNavigate, useLocation } from "react-router-dom";

const { Dragger } = Upload;

interface MetricUnit {
  id: number;
  name: string;
}

interface MetricOption {
  label: string;
  metric_units: MetricUnit[];
}

const STATUS = {
  TODO: 1,
  IN_PROGRESS: 2,
  ON_HOLD: 3,
  COMPLETED: 4,
  DEFAULT: 5,
};

function convertNullString(
  value: string | null | undefined
): string | null | undefined {
  if (value === "Null" || value === "null" || value === "NULL") {
    return null;
  }
  return value;
}

export default function UploadCSVModal({
  visible,
  onClose,
  data,
}: {
  visible: boolean;
  onClose: () => void;
  data: {
    name: string;
    start_line: string;
    dead_line: string;
    objective: string;
    purpose: string;
    idea: string;
    initialUsers: Array<{ id: number }>;
    files: Array<{ id: number }>;
    newTools: Array<{ name: string }>;
    toolsAdded: Array<{ id: number; isNew: boolean }>;
    criteria_tags: Array<{
      tag_name: string;
      weight: number;
      color: string;
    }>;
  };
}) {
  const { pathname } = useLocation();
  const navigate = useNavigate();
  const dispatch = useDispatch();
  const [step, setStep] = useState(1);
  const [csvError, setCSVError] = useState<string>("");
  const [fileUploaded, setFileUploaded] = useState(false);
  const [isLoading, setIsLoading] = useState(false);
  const [file, setFile] = useState<File | null>(null);
  const [fileList, setFileList] = useState<UploadFile[]>([]);
  const [errorMessage, setErrorMessage] = useState<string>("");
  const isShared = pathname.includes("shared-project");

  const getMetric = useSelector((state: any) => state?.plan?.predefinedMetrics);

  const uploadCSVData = useSelector(
    (state: any) => state?.plan?.plan?.uploadCSVData
  );

  const handleClose = (data: any) => {
    if (data?.categories?.length > 0) {
      setStep(2);
    } else {
      setCSVError(
        "There was an error uploading your CSV file. Please check the format and try again."
      );
    }
    setIsLoading(false);
  };

  useEffect(() => {
    // Skip validation if data or uploadCSVData is not available
    if (!data || !uploadCSVData?.categories) {
      setErrorMessage("");
      return;
    }

    const currentDate = new Date();
    const projectStartLineStr = data?.start_line;
    const projectDeadlineStr = data?.dead_line;

    // Convert to Date objects
    const projectStartLine = projectStartLineStr
      ? new Date(projectStartLineStr)
      : null;
    const projectDeadline = projectDeadlineStr
      ? new Date(projectDeadlineStr)
      : null;

    // Collect all validation errors
    const errors = [];

    // Validate project start date is before deadline
    if (
      projectStartLine &&
      projectDeadline &&
      projectStartLine > projectDeadline
    ) {
      errors.push("Project start date must be before the due date.");
    }

    // Helper function to format dates consistently
    const formatDate = (date: Date): string => {
      return date.toLocaleDateString("en-US", {
        year: "numeric",
        month: "short",
        day: "numeric",
      });
    };

    // Helper function to get category display name
    const getCategoryName = (category: any, index: number): string => {
      return category.name || `#${index + 1}`;
    };

    // Helper function to validate date relationships with consistent same-day handling
    const validateDateRelationship = (
      date1: Date | null,
      date2: Date | null,
      errorMessage: (date1Str: string, date2Str: string) => string
    ): string | null => {
      if (!date1 || !date2) return null;

      // Allow same day (equal dates)
      if (date1.toDateString() === date2.toDateString()) return null;

      // Check if date1 is after date2
      if (date1 > date2) {
        return errorMessage(formatDate(date1), formatDate(date2));
      }

      return null;
    };

    // Validate each category's dates
    uploadCSVData.categories.forEach((category: any, index: number) => {
      const categoryName = getCategoryName(category, index);
      const categoryStartLineStr = category?.start_line;
      const categoryDeadLineStr = category?.dead_line;
      const categoryStartLine = categoryStartLineStr
        ? new Date(categoryStartLineStr)
        : null;
      const categoryDeadLine = categoryDeadLineStr
        ? new Date(categoryDeadLineStr)
        : null;

      // Validate category start and end dates if both are provided
      if (categoryStartLine && categoryDeadLine) {
        // Validate category start date is before category deadline
        const startBeforeDeadlineError = validateDateRelationship(
          categoryStartLine,
          categoryDeadLine,
          (start, end) =>
            `Category "${categoryName}" start date (${start}) must be before its due date (${end}).`
        );

        if (startBeforeDeadlineError) errors.push(startBeforeDeadlineError);
      }

      // Validate category start date against project dates
      if (categoryStartLine) {
        // Validate category start date is before project deadline
        if (projectDeadline) {
          const startBeforeProjectDeadlineError = validateDateRelationship(
            categoryStartLine,
            projectDeadline,
            (catStart, projEnd) =>
              `Category "${categoryName}" start date (${catStart}) must be before project plan due date (${projEnd}).`
          );

          if (startBeforeProjectDeadlineError)
            errors.push(startBeforeProjectDeadlineError);
        }

        // Validate category start date is after project start date
        if (projectStartLine) {
          const startAfterProjectStartError = validateDateRelationship(
            projectStartLine,
            categoryStartLine,
            (projStart, catStart) =>
              `Category "${categoryName}" start date (${catStart}) must be after project plan start date (${projStart}).`
          );

          if (startAfterProjectStartError)
            errors.push(startAfterProjectStartError);
        }
      }

      // Validate category due date against project dates
      if (categoryDeadLine) {
        // Validate category due date is before project deadline
        if (projectDeadline) {
          const deadlineBeforeProjectDeadlineError = validateDateRelationship(
            categoryDeadLine,
            projectDeadline,
            (catEnd, projEnd) =>
              `Category "${categoryName}" due date (${catEnd}) must be before project plan due date (${projEnd}).`
          );

          if (deadlineBeforeProjectDeadlineError)
            errors.push(deadlineBeforeProjectDeadlineError);
        }

        // Validate category due date is after project start date
        if (projectStartLine) {
          const deadlineAfterProjectStartError = validateDateRelationship(
            projectStartLine,
            categoryDeadLine,
            (projStart, catEnd) =>
              `Category "${categoryName}" due date (${catEnd}) must be after project plan start date (${projStart}).`
          );

          if (deadlineAfterProjectStartError)
            errors.push(deadlineAfterProjectStartError);
        }
      }
    });

    // Set the first error message or clear if no errors
    setErrorMessage(errors.length > 0 ? errors[0] : "");
  }, [data, uploadCSVData]);

  const handleCloseModal = () => {
    onClose();
    setStep(1);
    setFileUploaded(false);
    setFile(null);
    setFileList([]);
    setIsLoading(false);
    setCSVError("");
  };

  const bulkApiClose = () => {
    navigate("/project-planner");
    handleCloseModal();
  };

  const handleContinue = async () => {
    if (step === 1) {
      if (!file) {
        message.error("Please upload a CSV file first");
        return;
      }

      try {
        setIsLoading(true);

        const formData = new FormData();
        formData.append("file", file as Blob);
        dispatch(uploadCSV(formData, handleClose));
      } catch (error) {
        message.error("Failed to upload CSV file");
        console.error("Upload error:", error);
        setCSVError(
          "There was an error uploading your CSV file. Please check the format and try again."
        );
        setIsLoading(false);
      }
    } else if (step === 2) {
      setIsLoading(true);

      const payload = {
        projectDetails: {
          name: data?.name,
          objective: data?.objective,
          purpose: data?.purpose,
          idea: data?.idea,
          start_line:
            data?.start_line === "Null"
              ? null
              : moment(data?.start_line).format("YYYY-MM-DD"),
          dead_line:
            data?.dead_line === "Null"
              ? null
              : moment(data?.dead_line).format("YYYY-MM-DD"),
        },
         criteria_tags: data.criteria_tags?.map((tag) => {
          return {
            name: tag?.tag_name,
            weight: tag?.weight,
            color: tag?.color,
          };
        }),
         resources: {
          people: data?.initialUsers?.map((user) => user.id),
          files: data?.files?.map((file) => file.id),
          tools: data?.newTools?.map((tool) => {
            return {
              name: tool.name,
            };
          }),
          tools_ids: data?.toolsAdded
            ?.map((tool) => !tool?.isNew && tool.id)
            .filter(Boolean),
        },
        categories: uploadCSVData?.categories?.map((category: any) => {
          const formattedStartDate =
            category?.start_line === "Null"
              ? null
              : moment(category?.start_line).format("YYYY-MM-DD");

          const formattedDeadline =
            category?.dead_line === "Null"
              ? null
              : moment(category?.dead_line).format("YYYY-MM-DD");

          const validatedStartDate =
            formattedStartDate === "Invalid date" ? null : formattedStartDate;

          const validatedDeadline =
            formattedDeadline === "Invalid date" ? null : formattedDeadline;

          return {
            name: category?.name,
            duration: category?.duration,
            weight: category?.weight,
            start_line: validatedStartDate,
            dead_line: validatedDeadline,
            action_items: category?.action_item?.map((item: any) => {
              const metricUnitId =
                getMetric
                  ?.find(
                    (option: MetricOption) =>
                      option?.label ===
                      convertNullString(item?.measurement_label)
                  )
                  ?.metric_units?.find(
                    (option: MetricUnit) =>
                      option?.name === convertNullString(item?.measurement_unit)
                  )?.id || null;

              const formattedDeadline =
                item?.dead_line === "Null"
                  ? null
                  : moment(item?.dead_line).format("YYYY-MM-DD");

              const formattedStartDate =
                item?.start_line === "Null"
                  ? null
                  : moment(item?.start_line).format("YYYY-MM-DD");

              const validatedDeadline =
                formattedDeadline === "Invalid date" ? null : formattedDeadline;

              const validatedStartDate =
                formattedStartDate === "Invalid date"
                  ? null
                  : formattedStartDate;

              return {
                name: item?.name,
                weight: item?.weight,
                duration: item?.duration,
                start_line: validatedStartDate,
                dead_line: validatedDeadline,
                status:
                  item?.status === "completed"
                    ? STATUS.COMPLETED
                    : item?.status === "in_progress"
                    ? STATUS.IN_PROGRESS
                    : item?.status === "todo"
                    ? STATUS.TODO
                    : item?.status === "on_hold"
                    ? STATUS.ON_HOLD
                    : STATUS.DEFAULT,
                plan_progress: Number(item?.plan_progress),
                metric_unit: metricUnitId,
                measurement_label: convertNullString(item?.measurement_label),
                measurement_unit: convertNullString(item?.measurement_unit),
                description: convertNullString(item?.description),
                responsible_person: item?.responsible_person || null,
              };
            }),
          };
        }),
      };

      try {
        if (!payload.projectDetails?.name || !payload.categories?.length) {
          throw new Error("Invalid project data");
        }

        await dispatch(BulkAriaProjectPlan(payload, bulkApiClose));
      } catch (error) {
        let errorMessage = "Failed to create project plan";
        if (error instanceof Error) {
          if (error.message === "Invalid project data") {
            errorMessage = "Invalid project data. Please check your CSV file.";
          } else {
            console.error("Bulk creation error:", error);
            errorMessage =
              "An error occurred while creating the project plan. Please try again.";
          }
        }

        message.error(errorMessage);
        setIsLoading(false);
      }
    }
  };

  const props: UploadProps = {
    name: "file",
    accept: ".csv",
    maxCount: 1,
    multiple: false,
    fileList: fileList,
    onRemove: () => {
      setFileUploaded(false);
      setFile(null);
      setFileList([]);
      return true;
    },
    customRequest: ({ file, onSuccess, onError }) => {
      try {
        const reader = new FileReader();
        reader.onload = (e) => {
          setFileUploaded(true);
          setFile(file as File);
          setFileList([
            {
              uid: "-1",
              name: (file as File).name,
              status: "done",
              size: (file as File).size,
              type: (file as File).type,
            },
          ]);
          onSuccess?.("ok");
        };
        reader.onerror = (e) => {
          setCSVError("Failed to read the file. Please try again.");
          message.error("Error reading file");
          onError?.(new Error("File read error"));
        };
        reader.readAsText(file as Blob);
      } catch (error) {
        onError?.(error as Error);
      }
    },
    beforeUpload: (file) => {
      const isCSV = file.type === "text/csv" || file.name.endsWith(".csv");
      if (!isCSV) {
        message.error("You can only upload CSV files!");
        return Upload.LIST_IGNORE;
      }
      const isLt10M = file?.size / 1024 / 1024 < 10;
      if (!isLt10M) {
        message.error("File must be smaller than 10MB!");
        return Upload.LIST_IGNORE;
      }
      return true;
    },
  };

  const handleBack = () => {
    if (step === 1) {
      handleCloseModal();
    } else {
      setStep(1);
      setCSVError("");
    }
  };

  useEffect(() => {
    if (getMetric === undefined || getMetric?.length === 0) {
      if (!isShared) {
        dispatch(getPredefinedMetrics());
      }
    }
  }, [getMetric]);

  return (
    <Modal
      className={"deleteModal"}
      centered
      closable={false}
      title={false}
      maskClosable={false}
      footer={null}
      onCancel={handleCloseModal}
      open={visible}
      width={1080}
    >
      <div className={styles.uploadCSVModal_header}>
        <h3>{step === 1 ? "Upload CSV" : "Review Results"}</h3>
        <Cross onClick={handleCloseModal} className={"c-pointer"} />
      </div>

      <div className={styles.uploadCSVModal_step}>
        <h4>
          {step === 1
            ? "Create projects by uploading a CSV file containing predefined categories, action items, and goal structures."
            : "Review the list of tasks to ensure they are relevant, complete, and properly formatted before importing them"}
        </h4>
        <h5>Step {step} of 2</h5>
      </div>

      {step === 1 ? (
        <>
          <div className={styles.uploadCSVModal_body}>
            <div className={styles.uploadCSVModal_body_drag}>
              <Dragger {...props}>
                <DownloadUp />
                <p className={styles.uploadCSVModal_body_drag_text}>
                  <strong>Upload a file</strong> or drag and drop
                </p>
              </Dragger>
            </div>

            {csvError && (
              <div className={`${styles.error_message}`}>{csvError}</div>
            )}

            <h5 className={styles.uploadCSVModal_body_download}>
              Don't have the the file?{" "}
              <strong
                onClick={() => {
                  const link = document.createElement("a");
                  link.href = template;
                  link.download = "ProjectTemplate.csv";
                  document.body.appendChild(link);
                  link.click();
                  document.body.removeChild(link);
                }}
              >
                Download template
              </strong>
            </h5>
          </div>
        </>
      ) : (
        <div className={styles.table_body}>
          <ul className={styles.table_header}>
            <li>Category & Action Items</li>
            <li>Weight</li>
            <li>Measurement</li>
            <li>Start Date</li>
            <li>Due Date</li>
            <li>Duration</li>
          </ul>

          <div className={styles.table_body_wrapper}>
            {!uploadCSVData?.categories ||
            uploadCSVData.categories.length === 0 ? (
              <div className={styles.no_data}>
                <p>
                  No valid data found in the CSV file. Please check the format
                  and try again.
                </p>
              </div>
            ) : (
              uploadCSVData?.categories?.map((item: any, index: number) => {
                const startLine =
                  item?.start_line === "Null"
                    ? "--"
                    : new Date(item?.start_line).toLocaleDateString("en-US", {
                        month: "short",
                        day: "numeric",
                      });
                const deadline =
                  item?.dead_line === "Null"
                    ? "--"
                    : new Date(item?.dead_line).toLocaleDateString("en-US", {
                        month: "short",
                        day: "numeric",
                      });

                return (
                  <div className={styles.table_body_container} key={index}>
                    <div className={styles.table_body_category}>
                      <h2 className={styles.title}>{item?.name}</h2>
                      <span className={styles.weight}>--</span>
                      <span className={styles.measurement}>--</span>
                      <span
                        className={`${styles.deadline} ${
                          startLine === "Invalid Date" ? styles.invalidDate : ""
                        }`}
                      >
                        <span>{startLine}</span>
                        <Calender />
                      </span>
                      <span
                        className={`${styles.deadline} ${
                          deadline === "Invalid Date" ? styles.invalidDate : ""
                        }`}
                      >
                        <span>{deadline}</span>
                        <Calender />
                      </span>
                      <span className={styles.duration}>
                        {formatTimeString(item?.duration)}
                      </span>
                    </div>

                    {item?.action_item?.map((item: any, index: number) => {
                      const startLine =
                        item?.start_line === "Null"
                          ? "--"
                          : new Date(item?.start_line).toLocaleDateString(
                              "en-US",
                              {
                                month: "short",
                                day: "numeric",
                              }
                            );
                      const deadline =
                        item?.dead_line === "Null"
                          ? "--"
                          : new Date(item?.dead_line).toLocaleDateString(
                              "en-US",
                              {
                                month: "short",
                                day: "numeric",
                              }
                            );
                      return (
                        <div className={styles.table_body_item} key={index}>
                          <div className={styles.title}>
                            <h2>{item?.name}</h2>
                          </div>
                          <span className={styles.weight}>{item.weight}</span>
                          <span className={styles.measurement}>--</span>
                          <span
                            className={`${styles.deadline} ${
                              startLine === "Invalid Date"
                                ? styles.invalidDate
                                : ""
                            }`}
                          >
                            <span>{startLine}</span>
                            <Calender />
                          </span>
                          <span
                            className={`${styles.deadline} ${
                              deadline === "Invalid Date"
                                ? styles.invalidDate
                                : ""
                            }`}
                          >
                            <span>{deadline}</span>
                            <Calender />
                          </span>
                          <span className={styles.duration}>
                            {formatTimeString(item?.duration)}
                          </span>
                        </div>
                      );
                    })}
                  </div>
                );
              })
            )}
          </div>
        </div>
      )}
      <div className={styles.mainFooter}>
        <span className={styles.error}>
          {errorMessage && step === 2 ? errorMessage : "\u00A0"}
        </span>
        <div className={styles.uploadCSVModal_footer}>
          <button onClick={handleBack}>
            {step === 1 ? null : <ArrowLeft />}
            {step === 1 ? "Skip" : "Back"}
          </button>
          <button
            onClick={handleContinue}
            disabled={
              (!fileUploaded && step === 1) ||
              isLoading ||
              (!!errorMessage && step === 2)
            }
            className={
              (!fileUploaded && step === 1) ||
              isLoading ||
              (errorMessage && step === 2)
                ? styles.disabled
                : ""
            }
          >
            {isLoading ? (
              <LoadingOutlined />
            ) : (
              `${step === 1 ? "Continue" : "Create Project"}`
            )}
          </button>
        </div>
      </div>
    </Modal>
  );
}
